import { PHOTO_SLOTS, PHOTOS_SEED, SERVICE_DEFS } from './fixtures';
import { defOf, docsOf } from './logic';
import type { ServiceTask, SvcCode } from './types';

// Số liệu mẫu của Mint-Service-iPad / -iPad-Detail / -Phone. Không có tiền: dòng phí / chi phí nội bộ nằm sau quyền `money` (chưa mở).

/** Người đang xem của board điện thoại (PHONGNT, OPS hiện trường); board iPad dùng HOATTN. */
export const PHONE_ME = 'PHONGNT';

/** Board điện thoại lệch dữ liệu desktop ở một việc: CCF2612851 giao PHONGNT, đang ở bước 4/5, luồng Đỏ. */
export const PHONE_PATCH: Readonly<Record<string, Partial<ServiceTask>>> = { CCF2612851: { who: 'PHONGNT', step: 3, lane: 'Đỏ' } };

/** Người xử lý / vai ở cột "SOP bước" của timeline (board: OPS · tự giao, CS, OPS chứng từ, Khách, OPS chứng từ). */
const STEP_WHO = ['OPS · tự giao', 'CS', 'OPS chứng từ', 'Khách', 'OPS chứng từ'] as const;

export interface StepRow {
  name: string;
  mark: string;
  state: 'done' | 'cur' | 'todo';
  who: string;
  when: string;
}

/** Timeline Quy trình: bước đã xong có giờ mẫu (20/Oct 9:15, 19/Oct 10:15 …), bước hiện tại "hạn dd/MMM", bước sau "chưa tới". */
export function stepRows(t: ServiceTask): StepRow[] {
  const steps = defOf(t.code).steps;
  const fin = t.status === 'Finish';
  return steps.map((name, j) => {
    const state = j < t.step || fin ? 'done' : j === t.step ? 'cur' : 'todo';
    return { name, mark: state === 'done' ? '✓' : String(j + 1), state, who: STEP_WHO[j % 5]!, when: state === 'done' ? `${20 - j}/Oct ${9 + j}:15` : state === 'cur' ? `hạn ${t.due.slice(0, 6)}` : 'chưa tới' };
  });
}

/** Lô hàng của việc: [nhãn, giá trị]. */
export const shipRows = (t: ServiceTask): [string, string][] => [
  ['Booking', t.booking], ['Hãng · tàu', `${t.carrier} · ${t.vessel}`], ['Tuyến', t.route], ['Cont', t.cont], ['Bãi / cảng', t.yard || '—'],
];

/** Ảnh hiện trường: [tên, đã có]. */
export const photoRows = (t: ServiceTask): [string, boolean][] => PHOTO_SLOTS[t.code].map((k) => [k, (PHOTOS_SEED[t.id] ?? []).includes(k)]);

export const DOC_LABEL = ['còn thiếu', 'đã nhận', 'không cần'] as const;
/** Hồ sơ giấy tờ cần: [tên, nhãn trạng thái]; "đã nhận / cần" = a/b. */
export function docRows(t: ServiceTask): { rows: [string, string][]; done: string } {
  const list = docsOf(t);
  const need = list.filter((x) => x[1] !== 2);
  return { rows: list.map(([k, s]) => [k, DOC_LABEL[s]]), done: `${need.filter((x) => x[1] === 1).length}/${need.length}` };
}

export interface PhoneGate {
  text: string;
  /** nhãn nút xử lý */
  go: string;
  /** ảnh cần chụp (nếu có) */
  photo?: string;
}

/** Điều kiện còn thiếu để rời bước, theo board điện thoại (ảnh hiện trường · phân luồng · ô hồ sơ bắt buộc). */
export function phoneGate(t: ServiceTask, photos: readonly string[] = PHOTOS_SEED[t.id] ?? []): PhoneGate[] {
  if (t.status === 'Finish') return [];
  const out: PhoneGate[] = [];
  const need = (k: string) => { if (!photos.includes(k)) out.push({ text: `Chưa có ảnh ${k.toLowerCase()}`, go: 'Chụp', photo: k }); };
  if (t.code === 'SCY' && t.step === 2) ['Cont rỗng', 'Seal', 'Đóng hàng'].forEach(need);
  if (t.code === 'SCY' && t.step === 3) need('Hạ bãi');
  if (t.code === 'CCF' && t.step === 2 && !t.lane) out.push({ text: 'Chưa ghi phân luồng', go: 'Ghi luồng' });
  if (t.code === 'CCF' && t.step === 3 && (t.lane === 'Đỏ' || t.lane === 'Soi chiếu') && !photos.includes('Kết quả kiểm hoá')) out.push({ text: `Luồng ${t.lane} · chưa có ảnh kiểm hoá`, go: 'Chụp', photo: 'Kết quả kiểm hoá' });
  return out;
}

/** Dòng "Thiếu: …" trên thẻ điện thoại. */
export const gateLine = (g: readonly PhoneGate[]): string => (g.length ? `Thiếu: ${g.map((x) => x.text.replace('Chưa có ', '').replace('Chưa nhập ', '')).join(', ')}` : '');

/** Tab loại dịch vụ của iPad theo thứ tự board. */
export const PAD_TABS: readonly SvcCode[] = ['CCF', 'COO', 'FUM', 'PTF', 'SCY', 'ISR', 'DDL', 'TRF'];
export const SERVICE_NAME = (c: SvcCode): string => SERVICE_DEFS.find((d) => d.code === c)!.name;


/** Việc có trên board iPad, theo thứ tự board (18 việc; board không có PTF2612845, FUM… và vài việc desktop khác). */
export const PAD_IDS = [
  'CCF2612870', 'COO2612871', 'CCF2612851', 'TRF2610078', 'CCF2612855', 'CCF2612858', 'CCF2612860', 'COO2612848', 'COO2612847', 'COO2612846', 'PTF2612849', 'FUM2612838', 'SCY2612821', 'SCY2612815', 'SCY2612823', 'DDL2612833', 'ISR2612830', 'COO2612844',
] as const;
